import { Dtos, SuretyAccountId, SuretyQuoteId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, createVersionConflictHandler } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { accountQueryKey } from "../../account/account.keys";
import {
	bondRequestBondFormQueryKey,
	bondRequestLatestUnderwritingQueryKey,
	bondRequestPreviewsQueryKey,
	bondRequestsQueryKey,
} from "../bond-request.keys";
import { redirectToIssuedBond } from "../utils";

type UpdateBondRequestCallbackOptions = {
	onSuccess?: () => void;
};

type UpdateBondRequestVariables = {
	data: Dtos.Admin.SuretyQuote.Update.Request;
	confirm?: boolean;
};

export const useAdminUpdateBondRequest = (
	id: SuretyQuoteId,
	accountId: SuretyAccountId,
	options?: { onConflict?: () => void; onBondingLineConflict?: () => void },
) => {
	const queryClient = useQueryClient();
	const { put } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const queryKeys = [
		bondRequestsQueryKey(id),
		bondRequestBondFormQueryKey(id),
		bondRequestLatestUnderwritingQueryKey(id),
		bondRequestPreviewsQueryKey(),
		accountQueryKey(accountId),
	];

	const mutation = useMutation({
		meta: { redirectOnError: redirectToIssuedBond },
		mutationKey: ["updateBondRequest", id],
		mutationFn: async ({ data, confirm }: UpdateBondRequestVariables) => {
			const url =
				confirm !== undefined
					? `/v2/admin/surety/accounts/${accountId}/quotes/${id}?confirmed=${confirm}`
					: `/v2/admin/surety/accounts/${accountId}/quotes/${id}`;
			await put(url, data, "none");
		},
		onError: (err: Error) => {
			if (err instanceof ApiError && err.key === "bonding_line_placement") {
				options?.onBondingLineConflict?.();
			} else {
				createVersionConflictHandler(queryKeys, queryClient, triggerErrorModal, {
					refetchType: "active",
					onConflict: options?.onConflict,
				})(err);
			}
		},
		onSuccess: () => {
			queryKeys.forEach((queryKey) => {
				queryClient.invalidateQueries({ queryKey });
			});
		},
	});

	return {
		updateBondRequest: (
			data: Dtos.Admin.SuretyQuote.Update.Request,
			callbackOptions?: UpdateBondRequestCallbackOptions & { confirm?: boolean },
		) => {
			mutation.mutate(
				{ data, confirm: callbackOptions?.confirm },
				{ onSuccess: callbackOptions?.onSuccess },
			);
		},
		updateBondRequestIsLoading: mutation.isPending,
	};
};
